import { useEffect, useReducer } from 'react';

import axios from '../../sharedModules/utils/axios.utils';
import { applyLiveDataset, DATA_SOURCE, markDataError, ProjectsDataset } from '../data/projectsData';

// Fetches the live dataset from the Rails API once per session and swaps it in. On failure it
// flags an error state — there is no bundled fake-data fallback.
type LoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

let status: LoadStatus = 'idle';
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

const loadOnce = async (): Promise<void> => {
  if (status !== 'idle') return;
  status = 'loading';
  notify();
  try {
    const response: any = await axios.get('/projects/dataset', { params: { location: 'tampa' } });
    const data: ProjectsDataset = response?.data?.result ?? response?.data;
    if (data && Array.isArray(data.jobs)) {
      // Well-formed payload — apply it even if empty (a legitimate "no project data" result).
      applyLiveDataset(data);
      status = 'loaded';
    } else {
      markDataError();
      status = 'error';
    }
  } catch {
    markDataError();
    status = 'error';
  } finally {
    notify();
  }
};

export interface ProjectsDataState {
  source: 'idle' | 'live' | 'error';
  loading: boolean;
  error: boolean;
}

const useProjectsData = (): ProjectsDataState => {
  const [, forceRender] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    listeners.add(forceRender);
    loadOnce();
    return () => {
      listeners.delete(forceRender);
    };
  }, []);

  return { source: DATA_SOURCE, loading: status === 'loading', error: status === 'error' };
};

export default useProjectsData;
